import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { AlertTriangle, Phone } from 'lucide-react';
import { CONTACT_INFO, BRAND_INFO } from '../data/mock';

const MedicalDisclaimerPage = () => {
  usePageTitle(`Medical Disclaimer | ${BRAND_INFO.name}`,
    `${BRAND_INFO.name} is a health information platform, not a medical or diagnostic service. This page explains exactly what that means.`);

  return (
    <>
      <Header />
      <main className="pt-28 lg:pt-36 pb-24 bg-cream-100">
        <div className="container-page">
          <p className="eyebrow">Important</p>
          <h1 className="mt-4 font-display text-display-lg text-ink-900">Medical Disclaimer</h1>
          <p className="mt-4 text-ink-600">Last updated: July 2025</p>

          <div className="mt-10 p-6 rounded-3xl bg-brick-50 border border-brick-200 flex gap-4">
            <AlertTriangle className="w-6 h-6 text-brick-600 mt-1 shrink-0" />
            <div className="text-ink-800">
              <p className="font-display text-xl text-ink-900">{BRAND_INFO.name} is a health information platform. It is not a medical or diagnostic service.</p>
              <p className="mt-2 text-sm">It cannot diagnose, treat, cure, monitor, or prevent any condition. If you are experiencing chest pain, sudden shortness of breath, or any other emergency, call 911 or your local emergency number immediately.</p>
            </div>
          </div>

          <div className="mt-12 space-y-8 text-ink-700 leading-relaxed">
            <section>
              <h2 className="font-display text-2xl text-ink-900">No medical advice</h2>
              <p className="mt-3">All content on {BRAND_INFO.domain}, including the in-app dashboard, charts, suggested ranges, written articles, and any communications from {CONTACT_INFO.corporateName}, is provided for general informational purposes only. It is not medical advice, diagnosis, or treatment, and it must not be used as a substitute for the advice of a licensed healthcare professional.</p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-ink-900">Real lab values, contextual interpretation</h2>
              <p className="mt-3">The lab values returned to your dashboard are real laboratory results from accredited U.S. clinical labs (LabCorp, Quest Diagnostics, BioReference). The way we plot, group, and label those values is contextual and educational. The presence of a value in the &ldquo;in range&rdquo; band on our dashboard does not guarantee health, and a value flagged &ldquo;above range&rdquo; does not necessarily indicate disease. Always consult a licensed clinician about meaningful changes.</p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-ink-900">Uploaded PDFs</h2>
              <p className="mt-3">Lab results uploaded as PDFs are parsed automatically. We make a good-faith effort to extract every marker accurately and to normalize units, but parsing accuracy varies by PDF format and source lab. Always verify any uploaded value against the underlying PDF before drawing health conclusions.</p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-ink-900">No physician-patient relationship</h2>
              <p className="mt-3">Use of the Service does not create any physician-patient, provider-patient, or therapeutic relationship between you and {CONTACT_INFO.corporateName} or any of its employees, contractors, or partner clinicians. The licensed independent physician who signs your requisition is reviewing your order for safety and appropriateness; they are not your treating physician.</p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-ink-900">Educational content</h2>
              <p className="mt-3">Our written and video educational content is reviewed for clarity and accuracy by independent clinical writers, but it is general education — not personalized advice. Do not change medication, supplementation, exercise routines, or diet based on a single article or chart alone. Always consult your own physician.</p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-ink-900">Emergencies</h2>
              <p className="mt-3">If you suspect you are having a medical emergency, do not use this app. Call 911 or your local emergency number, or go to the nearest emergency department.</p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-ink-900">Contact</h2>
              <p className="mt-3">For questions about this disclaimer, contact {CONTACT_INFO.corporateName} at {CONTACT_INFO.email} or {CONTACT_INFO.phone}.</p>
            </section>
          </div>

          <div className="mt-12 p-6 rounded-3xl bg-cream-50 border border-cream-200 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-ink-800">
              <Phone className="w-5 h-5 text-brick-500" />
              <span>In a U.S. emergency, call <strong>911</strong>.</span>
            </div>
            <Link to="/contact" className="btn-brick">Contact support</Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default MedicalDisclaimerPage;
